import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QUICK_RANGE_OPTIONS, computeRange } from "@/lib/dateRanges";

// localStorage key for the "Only orders I placed myself" toggle. Sticky
// across page refreshes so a user who toggles it OFF (e.g. a business
// admin who wants to see colleagues' orders too) doesn't have to
// re-toggle on every visit. First-ever visit defaults to ON.
// Phase 7 rename: pre-Phase-7 the toggle asked "paid by me" and used
// the LS key ...-paid-by-me-on. We migrate the old value once so users
// who had toggled OFF don't unexpectedly get toggled back ON.
const PLACED_BY_ME_TOGGLE_LS_KEY = "amazon-invoice-placed-by-me-toggle-on";
const LEGACY_PAID_BY_ME_LS_KEY = "amazon-invoice-ownership-paid-by-me-on";

const RUN_STATUS_VARIANT = {
  pending: "secondary",
  scraping: "default",
  downloading_pdfs: "default",
  packaging: "default",
  completed: "default",
  failed: "destructive",
  cookie_expired: "destructive",
  data_loss_detected: "destructive",
};

const ACTIVE_STATES = new Set(["pending", "scraping", "downloading_pdfs", "packaging"]);
const TERMINAL_STATES = new Set([
  "completed",
  "failed",
  "cookie_expired",
  "data_loss_detected",
]);

// Phase 6 stepper definition. Order matters — the index drives progress.
// User-explicit copy: "Connecting" (no login happens; we use cached
// cookies) and "Bundling .zip" is shown as its own step so users see
// the packaging phase progressing.
const RUN_STEPS = [
  { key: "connecting",  label: "Connecting" },
  { key: "scraping",    label: "Reading Orders" },
  { key: "downloading", label: "Downloading Invoices" },
  { key: "packaging",   label: "Bundling .zip" },
  { key: "done",        label: "Done" },
];

function isoToday() { return new Date().toISOString().slice(0, 10); }

// Strips the leading ISO timestamp produced by the backend's log_event
// helper ("2026-06-30T09:46:38.778+00:00 …") so the friendly view
// reads like a sentence instead of a system log line.
function stripIsoTimestamp(line) {
  if (typeof line !== "string") return String(line ?? "");
  return line.replace(/^\d{4}-\d{2}-\d{2}T[\d:.+-]+\s+/, "");
}
function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function isoYearsAgo(n) {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return d.toISOString().slice(0, 10);
}
function humanRange(startIso, endIso) {
  if (!startIso || !endIso) return { label: "", days: 0, amber: false };
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) {
    return { label: "", days: 0, amber: false };
  }
  if (e < s) return { label: "End date is before start date", days: 0, amber: true };
  const days = Math.round((e - s) / 86_400_000) + 1;
  let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) months -= 1;
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const parts = [];
  if (years) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (remMonths) parts.push(`${remMonths} month${remMonths === 1 ? "" : "s"}`);
  if (!parts.length) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  return {
    label: `Range: ${startIso} → ${endIso} (${parts.join(", ")})`,
    days,
    amber: days > 183,
  };
}
function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function formatEta(seconds) {
  if (!seconds || seconds < 1) return null;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

// Map run status / current_phase to a stepper index. Returns -1 when
// the run hasn't started yet so the stepper renders all-pending.
function deriveStepIndex(run) {
  if (!run) return -1;
  if (run.status === "completed") return 4;
  if (run.status === "packaging") return 3;
  if (run.status === "downloading_pdfs") return 2;
  if (run.status === "scraping") return 1;
  if (run.status === "pending") return 0;
  // Terminal-failure states: pin to whichever step the run was on.
  if (run.status === "failed" || run.status === "cookie_expired" || run.status === "data_loss_detected") {
    if (run.current_phase === "packaging") return 3;
    if (run.current_phase === "downloading_pdfs" || run.current_phase === "downloading_pdfs_resume") return 2;
    if (run.current_phase === "scraping") return 1;
    return 0;
  }
  return 0;
}

export default function Run() {
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(isoToday());
  // Phase 6+ — Quick-range preset dropdown. Default "custom" so the
  // manual date defaults above keep driving the form on first load.
  // Setting a preset auto-fills both date inputs; manually editing
  // either input snaps the dropdown back to "custom".
  const [quickRange, setQuickRange] = useState("custom");
  // Phase 7 — placed-by toggle. ON sends `placed_by_filter: "me"`
  // (client-side filter to orders the user personally placed), OFF
  // sends `"any"`. Default ON, sticky across refreshes via
  // localStorage. Also honours the pre-Phase-7 LS key on first load
  // so we don't reset a user who already toggled OFF.
  const [placedByMeOn, setPlacedByMeOn] = useState(() => {
    try {
      const stored = localStorage.getItem(PLACED_BY_ME_TOGGLE_LS_KEY);
      if (stored === "false") return false;
      if (stored === "true") return true;
      // First read — migrate from the pre-Phase-7 key if present.
      const legacy = localStorage.getItem(LEGACY_PAID_BY_ME_LS_KEY);
      if (legacy === "false") return false;
      return true;
    } catch {
      return true;
    }
  });
  const [settings, setSettings] = useState(null);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTechLog, setShowTechLog] = useState(false);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  // Quick-range presets compute their own dates; setting one fills
  // the manual inputs and leaves them editable (manual edits then
  // snap the dropdown back to "custom" via the handlers below).
  const handleQuickRangeChange = (val) => {
    setQuickRange(val);
    const r = computeRange(val);
    if (r) {
      setStartDate(r.start);
      setEndDate(r.end);
    }
  };
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    if (quickRange !== "custom") setQuickRange("custom");
  };
  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    if (quickRange !== "custom") setQuickRange("custom");
  };

  // Persist the placed-by toggle on every change so a refresh keeps
  // the user's last choice. localStorage may throw in private-mode
  // tabs — swallow silently, the default state is still ON.
  useEffect(() => {
    try {
      localStorage.setItem(PLACED_BY_ME_TOGGLE_LS_KEY, String(placedByMeOn));
    } catch { /* ignore quota / private-mode errors */ }
  }, [placedByMeOn]);

  const loadSettings = async () => {
    try {
      const res = await api.get("/settings");
      setSettings(res.data);
    } catch (e) {
      console.warn("settings load failed:", detailFromError(e));
    }
  };

  const loadCurrentRun = async () => {
    if (!currentRunId) return;
    try {
      const res = await api.get(`/runs/${currentRunId}`);
      setCurrentRun(res.data);
    } catch (e) {
      console.warn("run load failed:", detailFromError(e));
    }
  };

  useEffect(() => { loadSettings(); }, []);
  useEffect(() => {
    if (!currentRunId) return;
    loadCurrentRun();
    pollRef.current = setInterval(loadCurrentRun, 1500);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [currentRunId]);

  useEffect(() => {
    if (currentRun && TERMINAL_STATES.has(currentRun.status) && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [currentRun]);

  const disabledReason = useMemo(() => {
    if (!settings) return "Loading settings…";
    if (!settings.has_cookies) return "No cookies. Go to Settings to import.";
    const lv = settings.last_verify;
    if (!lv) return "Verify your session on Settings first.";
    if (lv.result !== "on_orders") return `Last verify was ${lv.result}. Re-verify on Settings.`;
    const ageHours = (Date.now() - new Date(lv.at).getTime()) / 3_600_000;
    if (ageHours > 24) return `Last verify was ${ageHours.toFixed(1)}h ago (>24h). Re-verify on Settings.`;
    if (currentRun && ACTIVE_STATES.has(currentRun.status)) return "Run already in progress.";
    return null;
  }, [settings, currentRun]);

  const startRun = async () => {
    if (disabledReason) return;
    setBusy(true);
    try {
      const res = await api.post("/runs", {
        start_date: startDate,
        end_date: endDate,
        // Phase 7 — client-side placed_by filter is the new primary
        // ownership control. `ownership_filter` stays "all" so
        // Amazon's server-side "Paid By You" filter never fires (it
        // over-includes colleagues' orders billed to the user's card
        // and under-includes user orders billed to a company card).
        ownership_filter: "all",
        placed_by_filter: placedByMeOn ? "me" : "any",
      });
      setCurrentRunId(res.data.run_id);
      toast.success(`Run started: ${res.data.run_id.slice(0, 8)}…`);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const isTerminal = currentRun && TERMINAL_STATES.has(currentRun.status);
  const inPdfPhase = currentRun?.status === "downloading_pdfs";

  // PDF phase derived state for ETA + percent
  const pdfStats = useMemo(() => {
    if (!currentRun) return null;
    const eligible = currentRun.pdfs_eligible || 0;
    const downloaded = currentRun.pdfs_downloaded || 0;
    const failed = currentRun.pdfs_failed || 0;
    const skipped = currentRun.pdfs_skipped || 0;
    const processed = downloaded + failed;
    const percent = eligible > 0 ? Math.round((processed / eligible) * 100) : 0;
    const avgSec = currentRun.avg_pdf_seconds || 0;
    const remaining = Math.max(eligible - processed, 0);
    const etaSeconds = avgSec > 0 ? remaining * avgSec : null;
    return { eligible, downloaded, failed, skipped, processed, percent, etaSeconds };
  }, [currentRun]);

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="run-page-title">Run</h1>
        <Link to="/runs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="history-link">
          View run history →
        </Link>
      </div>

      <Card data-testid="start-run-card">
        <CardHeader>
          <CardTitle>Start a new run</CardTitle>
          <CardDescription>Filters by order <span className="font-medium text-foreground">placed</span> date. Invoices and an Excel summary are bundled into a single download.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 md:max-w-sm" data-testid="quick-range-row">
            <Label htmlFor="quick-range-select">Quick range</Label>
            <Select value={quickRange} onValueChange={handleQuickRangeChange}>
              <SelectTrigger id="quick-range-select" data-testid="quick-range-select">
                <SelectValue placeholder="Custom" />
              </SelectTrigger>
              <SelectContent>
                {QUICK_RANGE_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    data-testid={`quick-range-option-${o.value}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Pick a preset to auto-fill the dates below — or leave it on
              Custom and pick dates by hand.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                min={isoYearsAgo(3)}
                max={isoToday()}
                onChange={handleStartDateChange}
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                min={isoYearsAgo(3)}
                max={isoToday()}
                onChange={handleEndDateChange}
                data-testid="end-date-input"
              />
            </div>
          </div>
          {(() => {
            const { label, amber } = humanRange(startDate, endDate);
            if (!label) return null;
            return (
              <p
                data-testid="date-range-preview"
                className={
                  amber
                    ? "text-xs font-medium text-amber-700 dark:text-amber-400"
                    : "text-xs text-muted-foreground"
                }
              >
                {label}
                {amber && " — long range, expect a multi-minute run"}
              </p>
            );
          })()}
          <div
            className="flex items-start gap-3 rounded-md border border-border bg-muted/30 px-3 py-3"
            data-testid="placed-by-toggle-row"
          >
            <Switch
              id="placed-by-toggle"
              checked={placedByMeOn}
              onCheckedChange={setPlacedByMeOn}
              data-testid="placed-by-toggle"
            />
            <div className="space-y-1">
              <Label htmlFor="placed-by-toggle" className="cursor-pointer">
                {placedByMeOn
                  ? "Only orders I placed myself"
                  : "All orders on this account"}
              </Label>
              <p
                className="text-xs text-muted-foreground"
                data-testid="placed-by-toggle-help"
              >
                {placedByMeOn
                  ? (settings?.user_display_name_full || settings?.user_display_name_hint)
                    ? `Matches Amazon's "Placed by ${settings.user_display_name_full || settings.user_display_name_hint}". We look at every order on the account, then keep only the ones placed by you.`
                    : "We'll first look at every order on the account, then keep only the ones Amazon labels as \"Placed by\" your name. Set your display name in Settings for accurate matching."
                  : "Keeping every order Amazon shows on this account — including orders your colleagues placed on shared accounts."}
              </p>
            </div>
          </div>
          <div
            className="flex items-start gap-3 rounded-md border border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-3"
            data-testid="phase7-invoice-banner"
          >
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-xs font-semibold"
            >
              ✓
            </span>
            <p className="text-xs text-emerald-900 dark:text-emerald-200 leading-relaxed">
              This tool now downloads the <span className="font-semibold">real GST invoices</span>
              {" "}and any <span className="font-semibold">credit notes</span> Amazon issued —
              not the &ldquo;Printable Order Summary&rdquo; page. Each order gets its own subfolder in the zip
              (<code className="font-mono text-[11px]">pdfs/&lt;order_id&gt;/invoice-01.pdf</code>).
            </p>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="memberships-tip">
            Tip: Prime renewals &amp; digital subscriptions live in Amazon&apos;s
            Memberships section, not Orders — they are excluded on purpose.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={startRun} disabled={busy || !!disabledReason} data-testid="start-run-button">
              Start Run
            </Button>
            {disabledReason && (
              <span className="text-sm text-muted-foreground" data-testid="start-disabled-reason">
                {disabledReason}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {currentRun && !isTerminal && (
        <Card data-testid="current-run-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Current run</CardTitle>
              <CardDescription>
                {currentRun.start_date} → {currentRun.end_date}
                {currentRun.placed_by_filter === "me" && (
                  <span data-testid="current-run-filter-chip" className="ml-2 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium">
                    only orders I placed
                  </span>
                )}
              </CardDescription>
            </div>
            <Badge
              variant={RUN_STATUS_VARIANT[currentRun.status] || "secondary"}
              className="uppercase tracking-wider"
              data-testid="current-run-status-badge"
            >
              {currentRun.status}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-5">
            <RunStepper run={currentRun} />

            <ExpectedTotalBanner run={currentRun} />

            <LiveCounters run={currentRun} pdfStats={pdfStats} />

            <PagesScannedRow run={currentRun} testid="current-run-pages-scanned" />

            {inPdfPhase && pdfStats && (
              <div className="space-y-2" data-testid="pdf-progress-block">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Invoices: {pdfStats.processed} / {pdfStats.eligible}</span>
                  <span>
                    ↓ {pdfStats.downloaded} · skipped {pdfStats.skipped} · failed {pdfStats.failed}
                    {pdfStats.etaSeconds ? ` · about ${formatEta(pdfStats.etaSeconds)} left` : ""}
                  </span>
                </div>
                <Progress value={pdfStats.percent} data-testid="pdf-progress-bar" />
              </div>
            )}

            <FriendlyLog
              run={currentRun}
              showTechLog={showTechLog}
              onToggle={setShowTechLog}
            />
          </CardContent>
        </Card>
      )}

      {currentRun && currentRun.status === "completed" && (
        <Card data-testid="completed-run-card">
          <CardHeader>
            <CardTitle>Run completed</CardTitle>
            <CardDescription data-testid="completed-run-summary">
              <CompletionSummary run={currentRun} />
              {currentRun.placed_by_filter === "me" && (
                <span data-testid="completed-run-filter-chip" className="ml-2 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium">
                  only orders I placed
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <PagesScannedRow run={currentRun} testid="completed-run-pages-scanned" />
            {currentRun.unhydrated_cards > 0 && (
              <div
                data-testid="unhydrated-warning"
                className="rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
              >
                ⚠ Amazon didn&apos;t fully load {currentRun.unhydrated_cards} order(s) this time.
                This sometimes happens during heavy traffic. To get the missing order(s),
                just run the same date range again — it usually works on the second try.
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild data-testid="download-zip-button">
                {/* Phase 7 post-mortem — zips MUST NOT use target="_blank".
                    The server sends Content-Disposition: attachment, so
                    Chrome triggers a download; combining that with a new
                    tab leaves an about:blank tab open in some setups.
                    The `download` attribute is a belt-and-braces hint. */}
                <a
                  href={`${API}/runs/${currentRun.id}/download`}
                  download={`amazon-invoices-${currentRun.id}.zip`}
                >
                  Download .zip ({formatBytes(currentRun.zip_size_bytes || 0)})
                </a>
              </Button>
              <a
                href={`${API}/runs/${currentRun.id}/download`}
                download={`amazon-invoices-${currentRun.id}.zip`}
                className="text-sm text-muted-foreground underline underline-offset-4"
                data-testid="redownload-link"
              >
                Re-download
              </a>
              <Button variant="outline" onClick={() => navigate(`/runs/${currentRun.id}`)} data-testid="view-orders-button">
                View orders
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {currentRun && currentRun.status === "cookie_expired" && (
        <Card data-testid="cookie-expired-card" className="border-destructive/40">
          <CardHeader>
            <CardTitle>Cookies expired mid-run</CardTitle>
            <CardDescription>
              We saved {currentRun.pdfs_downloaded} of {currentRun.pdfs_eligible} invoices before
              Amazon redirected to sign-in. A partial zip is still available.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {currentRun.download_ready && (
              <Button asChild data-testid="partial-download-button">
                <a
                  href={`${API}/runs/${currentRun.id}/download`}
                  download={`amazon-invoices-${currentRun.id}.zip`}
                >
                  Download partial .zip ({formatBytes(currentRun.zip_size_bytes || 0)})
                </a>
              </Button>
            )}
            <Link to="/settings" className="block text-primary underline underline-offset-4" data-testid="go-to-settings-link">
              Re-import cookies on Settings →
            </Link>
          </CardContent>
        </Card>
      )}

      {currentRun && currentRun.status === "failed" && (
        <Card data-testid="failed-run-card" className="border-destructive/40">
          <CardHeader>
            <CardTitle>Run failed</CardTitle>
            <CardDescription>{currentRun.error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/settings" className="text-primary underline underline-offset-4">
              Go to Settings →
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Phase 6 — stepper. Five steps, big visual progression. Uses
// status + current_phase to pick the active step. Failed runs pin to
// the step they died on and turn that pip red.
// ----------------------------------------------------------------------
function RunStepper({ run }) {
  const currentIdx = deriveStepIndex(run);
  const failed = run?.status === "failed" || run?.status === "cookie_expired" || run?.status === "data_loss_detected";
  return (
    <div className="flex items-center gap-1 sm:gap-2 flex-wrap" data-testid="run-stepper">
      {RUN_STEPS.map((s, i) => {
        let state = "pending";
        if (i < currentIdx) state = "done";
        else if (i === currentIdx) state = failed ? "failed" : "active";
        const pipClass = state === "done"
          ? "bg-emerald-600 text-white border-emerald-600"
          : state === "active"
            ? "bg-foreground text-background border-foreground animate-pulse"
            : state === "failed"
              ? "bg-destructive text-destructive-foreground border-destructive"
              : "bg-muted text-muted-foreground border-border";
        const labelClass = state === "pending"
          ? "text-muted-foreground"
          : state === "failed"
            ? "text-destructive font-medium"
            : state === "done"
              ? "text-foreground"
              : "text-foreground font-medium";
        return (
          <Fragment key={s.key}>
            <div className="flex items-center gap-2" data-testid={`stepper-step-${s.key}`}>
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium border ${pipClass}`}
                data-testid={`stepper-pip-${s.key}`}
                data-state={state}
              >
                {state === "done" ? "✓" : state === "failed" ? "!" : i + 1}
              </span>
              <span className={`text-xs sm:text-sm whitespace-nowrap ${labelClass}`}>
                {s.label}
              </span>
            </div>
            {i < RUN_STEPS.length - 1 && (
              <span
                className={`flex-1 h-px min-w-[12px] sm:min-w-[24px] ${
                  i < currentIdx ? "bg-emerald-600/60" : "bg-border"
                }`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// Phase 6 fix — Amazon's expected-total banner is now context-aware so
// the user is never confused by two numbers (e.g. "437" and "69")
// without an explanation. The banner morphs through three phases:
//
//   • Pre-probe — banner is hidden (no count yet).
//   • Post-probe + scrape just starting — explain that Amazon's count
//     is for the entire year and we'll narrow it to the user's range.
//   • Scrape in progress / complete — show the actual reconciliation:
//     "Year 2026 has about 437 orders. After your date range
//     (Jun 1 – Jun 30) we kept 69."
//
// Why a single banner instead of two side-by-side numbers: the user
// flagged that seeing "Amazon shows ~437" + "Orders found: 69" without
// context made it look like a bug. One banner that does the math
// removes any ambiguity.
// ----------------------------------------------------------------------
function _formatHumanDate(iso) {
  if (!iso) return iso;
  // ISO YYYY-MM-DD → "Jun 1, 2026"
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function _yearOf(iso) {
  if (!iso) return null;
  const m = /^(\d{4})/.exec(iso);
  return m ? m[1] : null;
}

function ExpectedTotalBanner({ run }) {
  const expected = run?.expected_orders_total;
  if (expected === undefined || expected === null) return null;
  const found = run?.total_orders_found ?? 0;
  // Year that Amazon's `#numOfOrdersMessage` was read from — we filter
  // the most recent year in the range first, so the most-recent-year
  // estimate is the one stored in `expected_orders_total`.
  const probedYear = _yearOf(run?.end_date);
  const startHuman = _formatHumanDate(run?.start_date);
  const endHuman = _formatHumanDate(run?.end_date);
  // Range label uses "Jun 1 – Jun 30" when both endpoints share a year,
  // otherwise full "Jun 1, 2025 – Jun 30, 2026".
  let rangeLabel = `${startHuman} – ${endHuman}`;
  if (probedYear && _yearOf(run?.start_date) === probedYear) {
    const shortStart = startHuman ? startHuman.replace(`, ${probedYear}`, "") : startHuman;
    const shortEnd   = endHuman   ? endHuman.replace(`, ${probedYear}`, "")   : endHuman;
    rangeLabel = `${shortStart} – ${shortEnd}, ${probedYear}`;
  }

  if (expected === 0) {
    return (
      <div
        data-testid="expected-total-banner"
        className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
      >
        Amazon shows <span className="font-medium text-foreground">0</span> orders
        for {probedYear ? `year ${probedYear}` : "the most recent year"} —
        we&apos;ll still check older years in your range.
      </div>
    );
  }

  // Once any orders have been read, swap to the reconciliation copy.
  if (found > 0) {
    return (
      <div
        data-testid="expected-total-banner"
        className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
      >
        {probedYear ? `Year ${probedYear}` : "The most recent year"} has about{" "}
        <span className="font-medium text-foreground">{expected}</span> orders on Amazon.
        After applying your date range ({rangeLabel}), we kept{" "}
        <span className="font-medium text-foreground">{found}</span>.
      </div>
    );
  }

  // Scrape just started — explain what Amazon's count covers and what
  // we'll narrow it to. This is the message that previously read
  // "Amazon shows about 437 orders for the most recent year" without
  // any hint that the final saved count will be smaller.
  return (
    <div
      data-testid="expected-total-banner"
      className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
    >
      Amazon shows about <span className="font-medium text-foreground">{expected}</span> orders
      for {probedYear ? `the whole of ${probedYear}` : "the most recent year"} — we&apos;ll keep
      only the ones inside your date range ({rangeLabel}).
    </div>
  );
}

// ----------------------------------------------------------------------
// Live counters block — four big tiles that update every 1.5s. Reads
// directly from the run document (no derived state needed).
// ----------------------------------------------------------------------
function LiveCounters({ run, pdfStats }) {
  if (!run) return null;
  const found = run.total_orders_found ?? 0;
  const downloaded = pdfStats?.downloaded ?? (run.pdfs_downloaded || 0);
  const skipped = pdfStats?.skipped ?? (run.pdfs_skipped || 0);
  const failed = pdfStats?.failed ?? (run.pdfs_failed || 0);
  // Phase 6 fix — label is "Orders in your date range" (not "Orders
  // found"). This is the count AFTER the date floor is applied so the
  // user understands why it may be smaller than Amazon's
  // `#numOfOrdersMessage` estimate on the dashboard banner above.
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="live-counters">
      <Counter
        label="Orders in your date range"
        value={found}
        testid="counter-orders-found"
      />
      <Counter
        label="Invoices saved"
        value={downloaded}
        accent="success"
        testid="counter-invoices-saved"
      />
      <Counter
        label="Skipped (no invoice)"
        value={skipped}
        testid="counter-invoices-skipped"
      />
      <Counter
        label="Failed (will retry)"
        value={failed}
        accent={failed > 0 ? "warn" : undefined}
        testid="counter-invoices-failed"
      />
    </div>
  );
}

function Counter({ label, value, accent, testid }) {
  const accentClass = accent === "success"
    ? "border-emerald-600/30 bg-emerald-50/50 dark:bg-emerald-950/20"
    : accent === "warn"
      ? "border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/20"
      : "border-border";
  return (
    <div className={`rounded-md border p-3 ${accentClass}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums" data-testid={testid}>
        {String(value)}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Friendly log — default view, toggled to dev log via a switch. Falls
// back to dev log when the run has no log_friendly array (pre-Phase 5c
// historical runs).
// ----------------------------------------------------------------------
function FriendlyLog({ run, showTechLog, onToggle }) {
  if (!run) return null;
  const hasFriendly = Array.isArray(run.log_friendly) && run.log_friendly.length > 0;
  const linesSource = showTechLog || !hasFriendly ? (run.log || []) : (run.log_friendly || []);
  const displayLines = linesSource.slice(-15).map(stripIsoTimestamp);
  return (
    <div data-testid="friendly-log-block">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs text-muted-foreground">
          {showTechLog ? "Technical log (last lines)" : "Activity (last lines)"}
        </Label>
        <div className="flex items-center gap-2">
          {!hasFriendly && (
            <span
              data-testid="tech-log-only-notice"
              className="text-[10px] text-muted-foreground italic"
            >
              technical only (older run)
            </span>
          )}
          <Switch
            id="show-tech-log"
            checked={showTechLog}
            onCheckedChange={onToggle}
            disabled={!hasFriendly}
            data-testid="show-tech-log-toggle"
          />
          <Label
            htmlFor="show-tech-log"
            className="text-xs text-muted-foreground cursor-pointer"
          >
            Show technical log
          </Label>
        </div>
      </div>
      <ScrollArea className="h-48 rounded-md border bg-muted/30 mt-1">
        <div
          className={`${showTechLog ? "font-mono text-xs" : "text-sm"} p-3 space-y-1`}
          data-testid={showTechLog ? "run-log-tail" : "friendly-log-tail"}
        >
          {displayLines.length === 0 ? (
            <div className="text-muted-foreground italic">No activity yet…</div>
          ) : (
            displayLines.map((l, i) => (
              <div key={i} className="leading-snug whitespace-pre-wrap break-words">{l}</div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ----------------------------------------------------------------------
// Completion summary — replaces the old single-line CardDescription so
// the user gets a clear breakdown of what was saved, skipped, and
// failed. Read straight from run counters.
// ----------------------------------------------------------------------
function CompletionSummary({ run }) {
  if (!run) return null;
  const total = run.total_orders_found ?? 0;
  const downloaded = run.pdfs_downloaded ?? 0;
  const skipped = run.pdfs_skipped ?? 0;
  const failed = run.pdfs_failed ?? 0;
  const parts = [];
  parts.push(<span key="total"><span className="font-medium text-foreground">{total}</span> order(s) found</span>);
  parts.push(<span key="d"><span className="font-medium text-foreground">{downloaded}</span> invoice(s) saved</span>);
  if (skipped > 0) {
    parts.push(
      <span key="s">
        <span className="font-medium text-foreground">{skipped}</span> skipped (no invoice — typically gift cards, digital downloads, or subscriptions)
      </span>
    );
  }
  if (failed > 0) {
    parts.push(
      <span key="f">
        <span className="font-medium text-amber-700 dark:text-amber-400">{failed} failed</span>
        {" — run the same range again to retry the failures"}
      </span>
    );
  }
  return (
    <span className="block space-y-0.5">
      {parts.map((p, i) => (
        // The inner spans (built above at lines ~861-877) already
        // carry stable string keys ("total", "d", "s", "f"). Prefer
        // those over the array index so React never confuses two
        // slices when this list grows in the future.
        <span key={p.key ?? `part-${i}`} className="block">
          {p}
          {i < parts.length - 1 ? "" : "."}
        </span>
      ))}
    </span>
  );
}

function PagesScannedRow({ run, testid }) {
  if (!run) return null;
  const scanned = Number(run.pages_scanned || 0);
  const expected = Number(run.expected_pages_total || 0);
  if (expected <= 0) return null;
  const ratio = expected > 0 ? scanned / expected : 1;
  // Only colour amber when the page loop exited via a bad reason
  // (next_disabled / max_pages / error). Date-filter cutoffs and
  // dedup_stopped are healthy early-exits even when the ratio is low.
  const BAD_REASONS = new Set(["next_disabled", "max_pages", "error"]);
  const reasons = run.pagination_exit_reason_by_year || {};
  const anyBadReason = Object.values(reasons).some((r) => BAD_REASONS.has(r));
  const isLagging = anyBadReason && ratio < 0.8;
  const byYear = run.pages_scanned_by_year || {};
  const byYearExpected = run.expected_pages_by_year || {};
  const yearKeys = Array.from(
    new Set([...Object.keys(byYear), ...Object.keys(byYearExpected)])
  ).sort((a, b) => b.localeCompare(a));
  return (
    <div
      data-testid={testid}
      className={
        "flex items-center justify-between rounded-md border px-3 py-2 text-sm flex-wrap gap-2 " +
        (isLagging
          ? "border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
          : "border-border bg-muted/30 text-muted-foreground")
      }
    >
      <span data-testid={`${testid}-summary`}>
        Pages scanned: <span className="font-medium text-foreground">{scanned}</span> / ~{expected} expected
      </span>
      {yearKeys.length > 0 && (
        <span className="text-xs" data-testid={`${testid}-breakdown`}>
          {yearKeys
            .map((y) => `${y}: ${byYear[y] || 0}/${byYearExpected[y] || 0}`)
            .join(" · ")}
        </span>
      )}
    </div>
  );
}
